import {
  Badge,
  Button,
  Input,
  Link,
  Select,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  mergeClasses,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import {
  Filter20Regular,
  Search20Regular,
  ArrowClockwise24Regular,
  ChevronRight24Regular,
  ChevronDown20Regular,
  CheckmarkCircle24Regular,
  Clock24Regular,
  DismissCircle24Regular,
  ErrorCircle24Regular,
  Prohibited24Regular,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { PageError } from '../../components/PageError/PageError.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';
import {
  isValidYyyyMmDd,
  useEvalRuns,
} from '../../hooks/useEvalRuns/useEvalRuns.js';
import type { EvalRunFilters } from '../../hooks/useEvalRuns/useEvalRuns.js';
import type {
  EvaluationRun,
  RunState,
} from '../../services/workflowApi.interface.js';
import { WorkflowApiService } from '../../services/workflowApi.js';
import { useUserBi } from '../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionScenario,
  ActionScenarioType,
  PanelRegion,
  PanelType,
} from '../../telemetry/userbi.js';

import { useEvalRunsPageStyles } from './EvalRunsPage.style.js';
import {
  getLoadedRunWindow,
  groupEvaluationRuns,
} from './groupEvaluationRuns.js';
import type { EvaluationRunGroup } from './groupEvaluationRuns.js';
import { RunCreator } from './RunCreator.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GROUP_BATCH_SIZE = 100;

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

interface StatusConfig {
  label: string;
  color: BadgeProps['color'];
  icon: React.ReactElement;
}

const STATUS_CONFIG: Record<RunState, StatusConfig> = {
  pending: {
    label: 'Pending',
    color: 'informative',
    icon: <Clock24Regular style={{ fontSize: 14 }} />,
  },
  running: {
    label: 'Running',
    color: 'brand',
    icon: <ArrowClockwise24Regular style={{ fontSize: 14 }} />,
  },
  completed: {
    label: 'Completed',
    color: 'success',
    icon: <CheckmarkCircle24Regular style={{ fontSize: 14 }} />,
  },
  failed: {
    label: 'Failed',
    color: 'danger',
    icon: <DismissCircle24Regular style={{ fontSize: 14 }} />,
  },
  cancelling: {
    label: 'Cancelling',
    color: 'warning',
    icon: <ErrorCircle24Regular style={{ fontSize: 14 }} />,
  },
  cancelled: {
    label: 'Cancelled',
    color: 'informative',
    icon: <Prohibited24Regular style={{ fontSize: 14 }} />,
  },
};

const ALL_STATUSES = 'All statuses';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatRangeDate(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

// ---------------------------------------------------------------------------
// RunRow
// ---------------------------------------------------------------------------

interface RunRowProps {
  run: EvaluationRun;
  workspaceName: string;
  showStatus: boolean;
  showPipeline: boolean;
  showBranch: boolean;
  group?: EvaluationRunGroup;
  onHistoryToggle?: (expanded: boolean) => void;
}

function RunRow({
  run,
  workspaceName,
  showStatus,
  showPipeline,
  showBranch,
  group,
  onHistoryToggle,
}: RunRowProps) {
  const styles = useEvalRunsPageStyles();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const cfg =
    run.runKind === 'external' && run.runState === 'completed'
      ? {
          ...STATUS_CONFIG.completed,
          color: 'brand' as const,
        }
      : (STATUS_CONFIG[run.runState] ?? STATUS_CONFIG.pending);
  const displayName = group?.name ?? run.displayName ?? run.id;
  const createdBy = run.systemProperties.createdBy;
  const createdDate = formatDate(run.systemProperties.createdDate);
  const isTerminal =
    run.runState === 'completed' ||
    run.runState === 'failed' ||
    run.runState === 'cancelled';
  const isSpinning =
    run.runState === 'running' || run.runState === 'cancelling';
  const ci = run.executionContext?.ciContext;
  let buildUrl: string | undefined;
  if (ci?.buildUrl) {
    try {
      const url = new URL(ci.buildUrl);
      if (url.protocol === 'https:' && !url.username && !url.password)
        buildUrl = url.href;
    } catch {
      // Older or external records may contain an invalid URL; keep the build ID visible.
    }
  }

  const openRun = (target: EvaluationRun) => {
    void navigate(
      `/workspaces/${encodeURIComponent(workspaceName)}/evaluation/evaluation-runs/${encodeURIComponent(target.id)}/report`,
    );
  };

  return (
    <>
      <TableRow className={styles.row}>
        <TableCell className={styles.cell}>
          {isTerminal ? (
            <Button
              appearance="transparent"
              className={styles.rowName}
              onClick={() => openRun(run)}
              aria-label={`View details for ${displayName}`}
              title={displayName}
            >
              {displayName}
            </Button>
          ) : (
            <div className={styles.rowName} title="Run is still in progress">
              {displayName}
            </div>
          )}
          <div className={styles.rowMeta}>
            {group ? (
              <Button
                appearance="subtle"
                size="small"
                className={styles.historyToggle}
                icon={
                  expanded ? (
                    <ChevronDown20Regular />
                  ) : (
                    <ChevronRight24Regular />
                  )
                }
                aria-label={`Show history for ${displayName}`}
                title={`Latest run ID: ${run.id}\nCreated by: ${createdBy ?? 'Unknown'}`}
                aria-expanded={expanded}
                aria-controls={`history-${run.id}`}
                onClick={() => {
                  onHistoryToggle?.(!expanded);
                  setExpanded(!expanded);
                }}
              >
                {group.runs.length} {group.runs.length === 1 ? 'run' : 'runs'}{' '}
                loaded
              </Button>
            ) : (
              <span>
                {run.runKind === 'external' ? 'External result' : 'Hosted run'}
              </span>
            )}
          </div>
        </TableCell>
        {showStatus && (
          <TableCell className={styles.cell}>
            <Badge
              className={styles.statusBadge}
              appearance="tint"
              color={cfg.color}
              size="large"
              icon={
                <span
                  className={mergeClasses(
                    styles.statusIcon,
                    isSpinning && styles.statusIconSpinning,
                  )}
                >
                  {cfg.icon}
                </span>
              }
              title={
                run.runKind === 'external' && run.runState === 'completed'
                  ? 'Result uploaded. This is not a quality pass/fail verdict.'
                  : 'Execution lifecycle, not evaluation quality.'
              }
            >
              {run.runKind === 'external' && run.runState === 'completed'
                ? 'Published'
                : cfg.label}
            </Badge>
          </TableCell>
        )}
        {showPipeline && (
          <TableCell className={styles.cell}>
            <div className={styles.primaryText}>
              {ci?.pipelineName ??
                (ci
                  ? 'Pipeline not recorded'
                  : run.runKind === 'hosted'
                    ? 'Hosted'
                    : 'External')}
            </div>
            <div className={styles.rowMeta}>
              {ci?.project && <span title={ci.project}>{ci.project}</span>}
              {ci?.system && <span>{ci.system}</span>}
              {ci?.buildId &&
                (buildUrl ? (
                  <Link
                    href={buildUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Open build ${ci.buildId}`}
                  >
                    #{ci.buildId}
                  </Link>
                ) : (
                  <span>#{ci.buildId}</span>
                ))}
            </div>
          </TableCell>
        )}
        {showBranch && (
          <TableCell className={styles.cell}>
            <span className={styles.branch} title={ci?.branch ?? undefined}>
              {ci?.branch?.replace(/^refs\/heads\//, '') ?? '-'}
            </span>
          </TableCell>
        )}
        <TableCell className={styles.cell}>
          <time
            dateTime={run.systemProperties.createdDate}
            title={run.systemProperties.createdDate}
          >
            {createdDate}
          </time>
        </TableCell>
        <TableCell className={styles.cell}>
          <RunCreator createdBy={createdBy} />
        </TableCell>
      </TableRow>
      {group && expanded && (
        <TableRow>
          <TableCell
            colSpan={
              3 + Number(showStatus) + Number(showPipeline) + Number(showBranch)
            }
            className={styles.historyCell}
          >
            <div
              id={`history-${run.id}`}
              role="region"
              aria-label={`Run history for ${group.name}`}
            >
              <div className={styles.historyCaption}>
                Run history, newest first.
              </div>
              <ul className={styles.historyList}>
                {group.runs.map((historyRun) => (
                  <li key={historyRun.id}>
                    <Button
                      appearance="subtle"
                      className={styles.historyRun}
                      disabled={
                        !['completed', 'failed', 'cancelled'].includes(
                          historyRun.runState,
                        )
                      }
                      aria-label={`View run ${historyRun.id} for ${group.name}`}
                      title={historyRun.id}
                      onClick={() => openRun(historyRun)}
                    >
                      <time dateTime={historyRun.systemProperties.createdDate}>
                        {formatRangeDate(
                          historyRun.systemProperties.createdDate,
                        )}
                      </time>
                      <span className={styles.historyCreator}>
                        <span>Created by:</span>
                        <RunCreator
                          createdBy={historyRun.systemProperties.createdBy}
                        />
                      </span>
                      <span className={styles.historyAction}>
                        {group.runs.some(
                          (entry) => entry.runState !== 'completed',
                        ) && (
                          <span>
                            {STATUS_CONFIG[historyRun.runState].label}
                          </span>
                        )}
                        <span className={styles.historyReport}>
                          View report
                        </span>
                      </span>
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

interface RunListProps {
  runs: EvaluationRun[];
  workspaceName: string;
  hasMore: boolean;
  onHistoryToggle: (expanded: boolean) => void;
}

function RunList({
  runs,
  workspaceName,
  hasMore,
  onHistoryToggle,
}: RunListProps) {
  const styles = useEvalRunsPageStyles();
  const groups = useMemo(() => groupEvaluationRuns(runs), [runs]);
  const loadedWindow = useMemo(() => getLoadedRunWindow(runs), [runs]);
  const displayedRuns = groups.map((group) => group.latest);
  const publishedOnly = displayedRuns.every(
    (run) => run.runKind === 'external' && run.runState === 'completed',
  );
  const showPipeline = displayedRuns.some((run) => {
    const ci = run.executionContext?.ciContext;
    return [ci?.pipelineName, ci?.buildId, ci?.project, ci?.system].some(
      Boolean,
    );
  });
  const showBranch = displayedRuns.some((run) =>
    Boolean(run.executionContext?.ciContext?.branch),
  );
  const rows = groups.map((group) => ({
    key: group.key,
    run: group.latest,
    group,
  }));
  return (
    <div className={styles.list}>
      <div className={styles.listHeader}>
        <span>
          {`${groups.length} ${groups.length === 1 ? 'evaluation' : 'evaluations'}`}
        </span>
        <span>
          {`${groups.reduce((count, group) => count + group.runs.length, 0)} runs loaded · Latest first`}
        </span>
      </div>
      {loadedWindow && (
        <div
          role="note"
          aria-label="Loaded history date range"
          className={styles.loadedRange}
        >
          <div>
            Created between{' '}
            <time dateTime={loadedWindow.oldest}>
              {formatRangeDate(loadedWindow.oldest)}
            </time>{' '}
            and{' '}
            <time dateTime={loadedWindow.newest}>
              {formatRangeDate(loadedWindow.newest)}
            </time>
            .
          </div>
          {hasMore && <div>More matching history is available.</div>}
        </div>
      )}
      <Table
        size="medium"
        aria-label="Evaluations"
        className={mergeClasses(
          styles.table,
          (showPipeline || showBranch) && styles.tableWithContext,
        )}
      >
        <TableHeader className={styles.tableHeader}>
          <TableRow>
            <TableHeaderCell className={styles.nameColumn}>
              Evaluation
            </TableHeaderCell>
            {!publishedOnly && (
              <TableHeaderCell className={styles.statusColumn}>
                Execution status
              </TableHeaderCell>
            )}
            {showPipeline && (
              <TableHeaderCell className={styles.pipelineColumn}>
                Pipeline / build
              </TableHeaderCell>
            )}
            {showBranch && (
              <TableHeaderCell className={styles.branchColumn}>
                Branch
              </TableHeaderCell>
            )}
            <TableHeaderCell className={styles.dateColumn}>
              {publishedOnly ? 'Latest published' : 'Latest run'}
            </TableHeaderCell>
            <TableHeaderCell>Created by</TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(({ key, run, group }) => (
            <RunRow
              key={key}
              run={run}
              group={group}
              workspaceName={workspaceName}
              showStatus={!publishedOnly}
              showPipeline={showPipeline}
              showBranch={showBranch}
              onHistoryToggle={onHistoryToggle}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EvalRunsPage
// ---------------------------------------------------------------------------

export function EvalRunsPage() {
  const styles = useEvalRunsPageStyles();
  const client = useHttpClient();
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const workspaceName = workspaceNameParam ?? '';
  const { activeName } = useWorkspaceContext();
  const navigate = useNavigate();
  const { account } = useAuth();
  const { recordAction } = useUserBi();

  // When the user switches workspace via the switcher while on this page,
  // redirect to the new workspace's eval-runs URL.
  useEffect(() => {
    if (activeName && workspaceName && activeName !== workspaceName) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/eval-runs`, {
        replace: true,
      });
    }
  }, [activeName, workspaceName, navigate]);
  const [searchParams, setSearchParams] = useSearchParams();
  const hasActiveFilters = [...searchParams.keys()].some(
    (key) => key !== 'view',
  );
  const search = searchParams.get('search') ?? '';
  const dateFilter = searchParams.get('date') ?? '';
  const status = searchParams.get('status') ?? '';
  const statusFilter = Object.prototype.hasOwnProperty.call(
    STATUS_CONFIG,
    status,
  )
    ? status
    : ALL_STATUSES;
  const myRunsOnly = searchParams.get('mine') === '1';
  const requestedRange = searchParams.get('range');
  const range =
    requestedRange === 'all' ||
    requestedRange === '24h' ||
    requestedRange === '7d' ||
    requestedRange === 'custom'
      ? requestedRange
      : isValidYyyyMmDd(dateFilter)
        ? 'all'
        : '24h';
  const filters: EvalRunFilters = {
    range,
    from: searchParams.get('from') ?? '',
    to: searchParams.get('to') ?? '',
    createdBy: myRunsOnly ? (account?.username ?? '') : undefined,
    origin: searchParams.get('origin') ?? '',
    ciSystem: searchParams.get('ci') ?? '',
    project: searchParams.get('project') ?? '',
    pipeline: searchParams.get('pipeline') ?? '',
    buildId: searchParams.get('build') ?? '',
    branch: searchParams.get('branch') ?? '',
    completeWindow: range !== 'all' || isValidYyyyMmDd(dateFilter),
  };
  const [showPipelineFilters, setShowPipelineFilters] = useState(
    [
      filters.ciSystem,
      filters.project,
      filters.pipeline,
      filters.buildId,
      filters.branch,
    ].some(Boolean),
  );
  const [showFilters, setShowFilters] = useState(
    myRunsOnly || Boolean(filters.origin) || showPipelineFilters,
  );

  const service = useMemo(() => new WorkflowApiService(client), [client]);
  const {
    runs,
    hasNextPage,
    isLoading,
    error,
    refresh,
    loadMore,
    isLoadingMore,
  } = useEvalRuns(
    service,
    workspaceName,
    1,
    GROUP_BATCH_SIZE,
    statusFilter,
    search,
    dateFilter,
    filters,
  );
  const hasCIContract = runs.some((run) =>
    Object.prototype.hasOwnProperty.call(run, 'executionContext'),
  );

  const recordControl = (control: string, active: boolean) =>
    recordAction({
      panelType: PanelType.EvaluationRunsPage,
      panelRegion: PanelRegion.Header,
      actionScenario: ActionScenario.FilterEvaluationRuns,
      actionScenarioType: ActionScenarioType.Submit,
      data: { filter: control, active },
    });
  const writeParams = (next: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    params.delete('view');
    for (const [key, value] of Object.entries(next)) {
      if (value) params.set(key, value);
      else params.delete(key);
      recordControl(key, Boolean(value));
    }
    setSearchParams(params, { replace: true });
  };

  if (!workspaceName) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          Select a workspace to view evaluation runs.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <div className={styles.title}>Eval Runs</div>
          <div className={styles.subtitle}>
            Find an evaluation, then explore its run history
          </div>
        </div>
        <Button
          appearance="outline"
          icon={<ArrowClockwise24Regular />}
          disabled={isLoading}
          onClick={() => {
            recordControl('refresh', true);
            refresh();
          }}
          aria-label="Refresh runs"
        >
          Refresh
        </Button>
      </div>

      {error && <PageError error={error} />}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Input
            className={styles.searchInput}
            placeholder="Search runs..."
            contentBefore={<Search20Regular />}
            aria-label="Search run name or ID"
            value={search}
            onChange={(_e, d) => writeParams({ search: d.value })}
          />
          <Select
            value={statusFilter}
            aria-label="Execution status"
            onChange={(_e, d) =>
              writeParams({ status: d.value === ALL_STATUSES ? '' : d.value })
            }
          >
            <option>{ALL_STATUSES}</option>
            <option>pending</option>
            <option>running</option>
            <option>completed</option>
            <option>failed</option>
            <option>cancelling</option>
            <option>cancelled</option>
          </Select>
          <Select
            aria-label="Created"
            value={range}
            onChange={(_e, d) =>
              writeParams({
                range: d.value,
                date: '',
                from: '',
                to: '',
              })
            }
          >
            <option value="all">All time</option>
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="custom">Custom dates (UTC)</option>
          </Select>
          <Button
            appearance={showFilters ? 'secondary' : 'subtle'}
            icon={<Filter20Regular />}
            aria-expanded={showFilters}
            aria-controls="eval-run-more-filters"
            onClick={() => {
              recordControl('moreFilters', !showFilters);
              setShowFilters(!showFilters);
            }}
          >
            More filters
          </Button>
          {dateFilter ? (
            <Button
              size="small"
              appearance="outline"
              onClick={() => writeParams({ date: '' })}
              aria-label={`Clear date filter ${dateFilter}`}
            >
              Date: {dateFilter} ✕
            </Button>
          ) : null}
        </div>
        <div className={styles.toolbarRight}>
          {hasActiveFilters && (
            <Button
              appearance="subtle"
              onClick={() => {
                recordControl('clear', false);
                setSearchParams(
                  {},
                  {
                    replace: true,
                  },
                );
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </div>

      {showFilters && (
        <div id="eval-run-more-filters" className={styles.filterRow}>
          <label className={styles.filterField} htmlFor="eval-run-source">
            Run source
            <Select
              id="eval-run-source"
              aria-label="Run source"
              value={filters.origin}
              onChange={(_e, d) => writeParams({ origin: d.value })}
            >
              <option value="">All sources</option>
              <option value="hosted">Hosted</option>
              <option value="external">External / CLI</option>
            </Select>
          </label>
          <Switch
            label="My runs only"
            checked={myRunsOnly}
            disabled={!account?.username}
            onChange={(_e, d) => writeParams({ mine: d.checked ? '1' : '' })}
          />
          <Button
            appearance="subtle"
            disabled={!hasCIContract}
            title={
              hasCIContract
                ? undefined
                : 'Pipeline filtering requires the updated backend API.'
            }
            aria-expanded={showPipelineFilters}
            aria-controls="eval-run-pipeline-filters"
            onClick={() => {
              recordControl('pipelineFilters', !showPipelineFilters);
              setShowPipelineFilters(!showPipelineFilters);
            }}
          >
            Pipeline filters
          </Button>
        </div>
      )}

      {range === 'custom' && (
        <div className={styles.filterRow}>
          <label className={styles.filterField} htmlFor="eval-run-from">
            From (UTC)
            <Input
              type="date"
              id="eval-run-from"
              aria-label="From (UTC)"
              value={filters.from}
              max={filters.to}
              onChange={(_e, d) => writeParams({ from: d.value })}
            />
          </label>
          <label className={styles.filterField} htmlFor="eval-run-to">
            To (UTC)
            <Input
              type="date"
              id="eval-run-to"
              aria-label="To (UTC)"
              value={filters.to}
              min={filters.from}
              onChange={(_e, d) => writeParams({ to: d.value })}
            />
          </label>
        </div>
      )}
      {showFilters && showPipelineFilters && (
        <div id="eval-run-pipeline-filters" className={styles.filterRow}>
          <label className={styles.filterField} htmlFor="eval-run-ci">
            CI system
            <Select
              aria-label="CI system"
              id="eval-run-ci"
              value={filters.ciSystem}
              onChange={(_e, d) => writeParams({ ci: d.value })}
            >
              <option value="">All CI systems</option>
              <option value="AzurePipelines">Azure Pipelines</option>
              <option value="GitHubActions">GitHub Actions</option>
              <option value="Generic">Generic CI</option>
            </Select>
          </label>
          {(
            [
              ['project', 'Project', filters.project],
              ['pipeline', 'Pipeline name', filters.pipeline],
              ['build', 'Build ID', filters.buildId],
              ['branch', 'Branch', filters.branch],
            ] as const
          ).map(([key, label, value]) => (
            <label
              key={key}
              className={styles.filterField}
              htmlFor={`eval-run-${key}`}
            >
              {label}
              <Input
                id={`eval-run-${key}`}
                aria-label={label}
                value={value ?? ''}
                onChange={(_e, d) => writeParams({ [key]: d.value })}
              />
            </label>
          ))}
        </div>
      )}

      {isLoading ? (
        <Spinner
          label={
            filters.completeWindow
              ? range === '24h'
                ? 'Loading all runs from the last 24 hours...'
                : 'Loading all runs in the selected date range...'
              : 'Loading runs...'
          }
        />
      ) : error && runs.length === 0 ? null : runs.length === 0 ? (
        <div className={styles.emptyState}>
          {hasActiveFilters
            ? 'No runs match your current filters.'
            : 'No evaluation runs found for this workspace.'}
        </div>
      ) : (
        <RunList
          runs={runs}
          workspaceName={workspaceName}
          hasMore={hasNextPage}
          onHistoryToggle={(active) => recordControl('history', active)}
        />
      )}

      {!filters.completeWindow && hasNextPage && (
        <Button
          className={styles.loadMore}
          appearance="subtle"
          disabled={isLoading || isLoadingMore}
          onClick={() => {
            recordControl('olderRuns', true);
            loadMore();
          }}
        >
          {isLoadingMore
            ? 'Fetching more history...'
            : `Show more history (up to ${GROUP_BATCH_SIZE} runs)`}
        </Button>
      )}
    </div>
  );
}
